import { useState, useEffect, useRef } from 'react';
import SEO, { PAGE_SEO } from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Bot, Send, Sparkles, Loader2, Trash2, ArrowLeft, MoreVertical, Check, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AIChat() {
  const { user, isAuthenticated } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatContainerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load chat history from localStorage
  useEffect(() => {
    const savedMessages = localStorage.getItem('skinuse_ai_chat');
    if (savedMessages) {
      setMessages(JSON.parse(savedMessages));
    } else {
      setMessages([{
        role: 'assistant',
        content: "Hello! 👋 I'm your SkinUse AI Beauty Advisor.\n\nI can help you with:\n• Skincare routines tailored to your skin type\n• Ingredient recommendations\n• Hair care tips\n• Product suggestions\n\nWhat would you like to know?",
        timestamp: Date.now()
      }]);
    }
  }, []);

  // Save messages to localStorage
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('skinuse_ai_chat', JSON.stringify(messages.slice(-50)));
    }
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: Date.now(),
      status: 'sent'
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const token = localStorage.getItem('skinuse_token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };

      const res = await fetch(`${BACKEND_URL}/api/ai/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          message: userMessage.content,
          session_id: sessionId,
          user_context: user ? {
            skin_concerns: user.skin_concerns || [],
            hair_concerns: user.hair_concerns || [],
            interests: user.interests || []
          } : null
        })
      });

      if (!res.ok) {
        throw new Error('Failed to get response');
      }

      const data = await res.json();
      
      // Update user message status to delivered
      setMessages(prev => prev.map((msg, idx) => 
        idx === prev.length - 1 && msg.role === 'user' 
          ? { ...msg, status: 'delivered' } 
          : msg
      ));
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        timestamp: Date.now()
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to get AI response. Please try again.');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I apologize, but I'm having trouble responding right now. Please try again in a moment.",
        timestamp: Date.now(),
        error: true
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const clearChat = () => {
    setMessages([{
      role: 'assistant',
      content: "Chat cleared! ✨ How can I help you with your skincare or haircare questions today?",
      timestamp: Date.now()
    }]);
    localStorage.removeItem('skinuse_ai_chat');
    toast.success('Chat history cleared');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestedQuestions = [
    "Morning routine for oily skin?",
    "How to reduce dark circles?",
    "Best ingredients for hair growth",
    "How to layer skincare?",
  ];

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, msg) => {
    const date = formatDate(msg.timestamp);
    if (!groups[date]) groups[date] = [];
    groups[date].push(msg);
    return groups;
  }, {});

  // Set page title
  useEffect(() => {
    document.title = 'AI Beauty Advisor | SkinUse';
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="ai-chat-page">
      <SEO {...PAGE_SEO.aiAdvisor} url="/ai-advisor" />
      
      {/* Desktop: Show Navbar */}
      <div className="hidden md:block">
        <Navbar />
      </div>
      
      {/* Mobile Chat Header - WhatsApp Style */}
      <div className="md:hidden sticky top-0 z-50 bg-gradient-to-r from-gold/20 to-gold/10 backdrop-blur-md border-b border-gold/20">
        <div className="flex items-center justify-between px-2 py-2">
          <div className="flex items-center gap-2">
            <Link to="/dashboard" className="p-2 -ml-1 hover:bg-black/5 rounded-full transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold to-gold/70 flex items-center justify-center shadow-md">
              <Sparkles className="w-5 h-5 text-black" />
            </div>
            <div className="flex flex-col">
              <span className="font-semibold text-sm">AI Beauty Advisor</span>
              <span className="text-xs text-muted-foreground">
                {loading ? 'typing...' : 'Online'}
              </span>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9">
                <MoreVertical className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={clearChat} className="text-destructive">
                <Trash2 className="w-4 h-4 mr-2" />
                Clear chat
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden md:block py-4 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold/10 rounded-sm mb-3">
            <Bot className="w-4 h-4 text-gold" />
            <span className="text-xs font-semibold uppercase tracking-wider text-gold">AI Advisor</span>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-serif text-2xl sm:text-3xl font-semibold">Beauty Advisor</h1>
              <p className="text-sm text-muted-foreground">Get personalized skincare & haircare advice</p>
            </div>
            <Button variant="ghost" size="sm" onClick={clearChat} className="text-muted-foreground">
              <Trash2 className="w-4 h-4 mr-1" />
              Clear
            </Button>
          </div>
        </div>
      </div>

      {/* Chat Container */}
      <main className="flex-1 flex flex-col overflow-hidden md:py-4 md:px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto w-full flex-1 flex flex-col overflow-hidden md:bg-card md:rounded-lg md:shadow-card md:border md:border-border">
          
          {/* Messages Area - WhatsApp Style Background */}
          <div 
            ref={chatContainerRef}
            className="flex-1 overflow-y-auto px-3 py-4 md:p-4"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4af37' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              backgroundColor: 'hsl(var(--background))'
            }}
          >
            {/* Date Groups */}
            {Object.entries(groupedMessages).map(([date, msgs]) => (
              <div key={date}>
                {/* Date Separator */}
                <div className="flex justify-center my-4">
                  <span className="px-3 py-1 bg-secondary/80 backdrop-blur-sm rounded-full text-xs text-muted-foreground shadow-sm">
                    {date}
                  </span>
                </div>
                
                {/* Messages */}
                {msgs.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex mb-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div 
                      className={`relative max-w-[85%] md:max-w-[70%] px-3 py-2 rounded-lg shadow-sm ${
                        msg.role === 'user' 
                          ? 'bg-gold text-black rounded-br-sm' 
                          : msg.error 
                            ? 'bg-destructive/10 text-destructive rounded-bl-sm'
                            : 'bg-card border border-border rounded-bl-sm'
                      }`}
                    >
                      {/* Message Tail */}
                      <div 
                        className={`absolute top-0 w-3 h-3 ${
                          msg.role === 'user' 
                            ? '-right-1.5 bg-gold' 
                            : '-left-1.5 bg-card border-l border-t border-border'
                        }`}
                        style={{
                          clipPath: msg.role === 'user' 
                            ? 'polygon(0 0, 100% 0, 0 100%)' 
                            : 'polygon(100% 0, 0 0, 100% 100%)'
                        }}
                      />
                      
                      {/* Message Content */}
                      <div className="text-sm leading-relaxed whitespace-pre-wrap">
                        {msg.content.split('\n').map((line, i) => {
                          const parts = line.split(/(\*\*.*?\*\*)/g);
                          return (
                            <span key={i}>
                              {parts.map((part, j) => {
                                if (part.startsWith('**') && part.endsWith('**')) {
                                  return <strong key={j}>{part.slice(2, -2)}</strong>;
                                }
                                return part;
                              })}
                              {i < msg.content.split('\n').length - 1 && <br />}
                            </span>
                          );
                        })}
                      </div>
                      
                      {/* Time & Status */}
                      <div className={`flex items-center gap-1 mt-1 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <span className={`text-[10px] ${msg.role === 'user' ? 'text-black/60' : 'text-muted-foreground'}`}>
                          {formatTime(msg.timestamp)}
                        </span>
                        {msg.role === 'user' && (
                          <span className="text-black/60">
                            {msg.status === 'delivered' ? (
                              <CheckCheck className="w-3.5 h-3.5" />
                            ) : (
                              <Check className="w-3.5 h-3.5" />
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
            
            {/* Typing Indicator */}
            {loading && (
              <div className="flex justify-start mb-2">
                <div className="relative max-w-[85%] px-4 py-3 rounded-lg shadow-sm bg-card border border-border rounded-bl-sm">
                  <div 
                    className="absolute top-0 -left-1.5 w-3 h-3 bg-card border-l border-t border-border"
                    style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }}
                  />
                  <div className="flex items-center gap-1">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-gold rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-gold rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-gold rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Suggested Questions - Only show on few messages */}
          {messages.length <= 2 && (
            <div className="px-3 py-2 bg-secondary/30 border-t border-border">
              <p className="text-xs text-muted-foreground mb-2">Quick questions:</p>
              <div className="flex flex-wrap gap-2">
                {suggestedQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => setInput(q)}
                    className="text-xs px-3 py-1.5 bg-card hover:bg-gold/10 border border-border rounded-full transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area - WhatsApp Style */}
          <div className="p-2 md:p-3 bg-secondary/50 border-t border-border">
            <div className="flex items-end gap-2">
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value);
                    // Auto-resize
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message..."
                  className="w-full px-4 py-2.5 bg-card border border-border rounded-3xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold/50 transition-all"
                  rows={1}
                  style={{ minHeight: '44px', maxHeight: '120px' }}
                  data-testid="chat-input"
                />
              </div>
              <Button 
                onClick={sendMessage} 
                disabled={!input.trim() || loading}
                className="h-11 w-11 rounded-full bg-gold hover:bg-gold/90 text-black shadow-md flex-shrink-0"
                data-testid="chat-send"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </div>
            {/* Disclaimer - Hidden on mobile for cleaner look */}
            <p className="hidden md:block text-xs text-muted-foreground mt-2 text-center">
              AI advice is for informational purposes. Consult a dermatologist for medical concerns.
            </p>
          </div>
        </div>
      </main>

      {/* Desktop Footer */}
      <div className="hidden md:block">
        <Footer />
      </div>
    </div>
  );
}
